// QR Kiosk v2 — KV2-7 presentational atoms: TaskStatusChip (designer TSTAT →
// tokens, with a defensive unknown-status fallback), TaskFileChipGrid (ClosingRemarks
// file chips → authenticated blob download; a plain <a href> 401s), and TaskPhotoTiles
// (images-only ≤3 capture grid for the note + done sheets). Tokens only; no MUI/dt/hex.
import React, { useState } from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { downloadAuthenticatedFile } from "../../../shared/hooks/useAuthenticatedBlobUrl";
import { ServiceReportFile } from "../../models/serviceReportsModels";
import { Glyph, GD } from "../shared/glyphs";
import { StatusPip } from "../shared/StatusPip";
import { usePressed } from "../shared/usePressed";
import { ErrorCallout } from "../shared/ErrorCallout";
import { isImageFileName } from "../reports/reportUtils";
import { taskChipMeta } from "./taskUtils";
import { KioskTaskFile } from "./kioskTasksModels";

const chipBase: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5, height: 22, padding: "0 9px",
  borderRadius: 999, fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.07em", textTransform: "uppercase",
};

export interface TaskStatusChipProps {
  kioskStatus: string;
  isOverdue: boolean;
}

export function TaskStatusChip({ kioskStatus, isOverdue }: TaskStatusChipProps) {
  const { translate } = useTranslation("public");

  // Server guarantees overdue ⟹ not done — the red chip replaces the status chip.
  if (isOverdue) {
    return (
      <span
        style={{
          ...chipBase,
          fontWeight: 700,
          background: "color-mix(in oklab, var(--red) 13%, var(--card))",
          border: "1px solid color-mix(in oklab, var(--red) 40%, var(--border))",
          color: "color-mix(in oklab, var(--red) 80%, var(--ink))",
        }}
      >
        <StatusPip tone="--red" size={6} />
        {translate("kioskV2TasksOverdue")}
      </span>
    );
  }

  const meta = taskChipMeta(kioskStatus);
  if (!meta.known) {
    // Future server value: raw string in a muted solid pill — never crash, never hide.
    return (
      <span
        style={{
          ...chipBase,
          fontWeight: 600,
          background: "color-mix(in oklab, var(--muted) 11%, var(--card))",
          border: "1px solid color-mix(in oklab, var(--muted) 30%, var(--border))",
          color: "var(--muted)",
        }}
      >
        {kioskStatus}
      </span>
    );
  }

  const { labelKey, tone, dashed } = meta;
  return (
    <span
      style={{
        ...chipBase,
        fontWeight: 600,
        background: `color-mix(in oklab, var(${tone}) 11%, var(--card))`,
        border: `1px ${dashed ? "dashed" : "solid"} color-mix(in oklab, var(${tone}) 38%, var(--border))`,
        color: `color-mix(in oklab, var(${tone}) 80%, var(--ink))`,
      }}
    >
      <StatusPip tone={tone} size={6} />
      {translate(labelKey)}
    </span>
  );
}

const fileChipBase: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 9, height: 46, padding: "0 11px", borderRadius: 10,
  border: "1px solid var(--border)", background: "var(--bg-soft)", minWidth: 0, fontFamily: "inherit",
  cursor: "pointer", textAlign: "left",
};

function TaskFileChip({ file, onDownload }: { file: KioskTaskFile; onDownload: () => void }) {
  const { pressHandlers, pressStyle } = usePressed();
  const isImg = isImageFileName(file.fileName);
  return (
    <button type="button" onClick={onDownload} {...pressHandlers} style={{ ...fileChipBase, ...pressStyle }}>
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background: isImg ? "color-mix(in oklab, var(--faint) 22%, transparent)" : "var(--card)",
          border: "1px solid var(--border-soft)",
          display: "inline-grid",
          placeItems: "center",
          color: "var(--muted)",
          flexShrink: 0,
        }}
      >
        <Glyph d={isImg ? GD.cam : GD.doc} size={14} />
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: "var(--ink-2)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {file.fileName}
      </span>
    </button>
  );
}

const FILE_DOWNLOAD_BASE = "/v1/customer/work-orders/files";

export function TaskFileChipGrid({ files }: { files: KioskTaskFile[] }) {
  const { translate } = useTranslation("public");
  const [failed, setFailed] = useState(false);

  const download = async (file: KioskTaskFile) => {
    setFailed(false);
    try {
      const base = (window as any).app.config.maintenanceApiBaseUrl;
      await downloadAuthenticatedFile(`${base}${FILE_DOWNLOAD_BASE}/${file.id}/download`, file.fileName);
    } catch {
      setFailed(true);
    }
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 11 }}>
        {files.map((f) => (
          <TaskFileChip key={f.id} file={f} onDownload={() => download(f)} />
        ))}
      </div>
      {failed && (
        <div style={{ marginTop: 8 }}>
          <ErrorCallout title={translate("kioskV2TaskActionFailed")} />
        </div>
      )}
    </>
  );
}

function RemoveTileButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="remove"
      onClick={onClick}
      style={{
        position: "absolute",
        top: 0,
        right: 0,
        padding: 4,
        border: "none",
        background: "transparent",
        cursor: "pointer",
        display: "grid",
        placeItems: "center",
      }}
    >
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 999,
          background: "color-mix(in oklab, var(--ink) 55%, transparent)",
          color: "var(--card)",
          display: "grid",
          placeItems: "center",
        }}
      >
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
          <path d="M6 6 L18 18" />
          <path d="M18 6 L6 18" />
        </svg>
      </span>
    </button>
  );
}

export interface TaskPhotoTilesProps {
  files: ServiceReportFile[];
  onAdd: (list: FileList) => void;
  onRemove: (index: number) => void;
  rejectedName: string | null;
  inputId: string;
  max: number;
}

// Images-only capture grid (accept=jpeg/png/webp; camera tile carries capture="environment").
// All staged files are images (imagesOnly opts) so every tile is a data-URI thumbnail.
export function TaskPhotoTiles({ files, onAdd, onRemove, rejectedName, inputId, max }: TaskPhotoTilesProps) {
  const { translate } = useTranslation("public");
  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) onAdd(e.target.files);
    e.target.value = "";
  };
  const canAdd = files.length < max;
  const tileBase: React.CSSProperties = {
    height: 82,
    borderRadius: 11,
    overflow: "hidden",
    position: "relative",
  };

  return (
    <div>
      <input type="file" id={`${inputId}-cam`} accept="image/jpeg,image/png,image/webp" capture="environment" style={{ display: "none" }} onChange={onInput} />
      <input type="file" id={`${inputId}-pick`} accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={onInput} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {canAdd && (
          <label
            htmlFor={`${inputId}-cam`}
            style={{
              ...tileBase,
              border: "1.5px dashed var(--tier-accent-line)",
              background: "var(--tier-accent-soft)",
              color: "var(--tier-accent)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              cursor: "pointer",
            }}
          >
            <Glyph d={GD.cam} size={20} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700 }}>{`${files.length}/${max}`}</span>
          </label>
        )}
        {files.map((f, i) => (
          <div
            key={`f-${i}`}
            style={{
              ...tileBase,
              border: "1px solid var(--border)",
              backgroundImage: `url(${f.content})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <RemoveTileButton onClick={() => onRemove(i)} />
          </div>
        ))}
        {canAdd && (
          <label
            htmlFor={`${inputId}-pick`}
            style={{
              ...tileBase,
              border: "1px dashed var(--border)",
              display: "grid",
              placeItems: "center",
              color: "var(--faint)",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            +
          </label>
        )}
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)", marginTop: 5 }}>
        {translate("kioskV2TaskPhotoLimit")}
      </div>
      {rejectedName && (
        <div style={{ fontSize: 10.5, color: "color-mix(in oklab, var(--amber) 78%, var(--ink))", marginTop: 4 }}>
          {translate("kioskV2TaskPhotoRejected", { name: rejectedName })}
        </div>
      )}
    </div>
  );
}
